// Whole-app boot under react-native-web — the correct harness architecture,
// since the app's import graph is fully connected through store→saga→navigation
// (isolated-screen rendering is impossible here). Mirrors index.js minus the
// native firebase background handler. App's codePush HOC is a passthrough shim.
import "react-native-gesture-handler";
import { AppRegistry, TouchableNativeFeedback } from "react-native";
import { enableScreens } from "react-native-screens";
import App from "../App";
import { name as appName } from "../app.json";

// Android-only statics that react-native-web omits. Components (e.g. DeliveryCard)
// call TouchableNativeFeedback.Ripple(...) for the `background` prop; without
// these the call throws "Ripple is not a function" and blanks the screen. We
// patch the shared RNW singleton at boot — every importer sees the same object.
if (TouchableNativeFeedback) {
  if (typeof TouchableNativeFeedback.Ripple !== "function")
    TouchableNativeFeedback.Ripple = (color, borderless) => ({
      type: "RippleAndroid",
      color,
      borderless,
    });
  if (typeof TouchableNativeFeedback.SelectableBackground !== "function")
    TouchableNativeFeedback.SelectableBackground = (rippleRadius) => ({
      type: "ThemeAttrAndroid",
      attribute: "selectableItemBackground",
      rippleRadius,
    });
  if (
    typeof TouchableNativeFeedback.SelectableBackgroundBorderless !== "function"
  )
    TouchableNativeFeedback.SelectableBackgroundBorderless = (
      rippleRadius,
    ) => ({
      type: "ThemeAttrAndroid",
      attribute: "selectableItemBackgroundBorderless",
      rippleRadius,
    });
  if (typeof TouchableNativeFeedback.canUseNativeForeground !== "function")
    TouchableNativeFeedback.canUseNativeForeground = () => false;
}

// RNW doesn't export ToastAndroid, but the app calls ToastAndroid.show/
// showWithGravity in ~9 places (wallet errors, image-upload limits, support
// tickets). Undefined → "Cannot read property 'show' of undefined" crash when
// triggered. Define it on the RN module object with a lightweight DOM toast.
// eslint-disable-next-line @typescript-eslint/no-require-imports
const _RN = require("react-native");
if (!_RN.ToastAndroid) {
  const showToast = (msg) => {
    try {
      const el = document.createElement("div");
      el.textContent = String(msg);
      el.style.cssText =
        "position:fixed;left:50%;bottom:84px;transform:translateX(-50%);max-width:80%;text-align:center;" +
        "background:rgba(0,0,0,0.85);color:#fff;padding:10px 16px;border-radius:20px;" +
        "font:13px -apple-system,system-ui,sans-serif;z-index:99999;pointer-events:none";
      document.body.appendChild(el);
      setTimeout(() => el.remove(), 2500);
    } catch (_e) {}
  };
  const ToastAndroid = {
    SHORT: 0,
    LONG: 1,
    TOP: 0,
    BOTTOM: 0,
    CENTER: 0,
    show: showToast,
    showWithGravity: showToast,
    showWithGravityAndOffset: showToast,
  };
  try {
    Object.defineProperty(_RN, "ToastAndroid", {
      value: ToastAndroid,
      configurable: true,
    });
  } catch (_e) {
    try {
      _RN.ToastAndroid = ToastAndroid;
    } catch (_) {}
  }
}

enableScreens();

// D+ per-CP web build routing: if a session already exists, re-check which web
// build this CP should run (the cookie may have expired). Guarded reload, see shims/web-build.js.
try {
  const bootToken = window.localStorage.getItem("user_token");
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  if (bootToken) require("./shims/web-build").checkWebBuild(bootToken);
} catch (_e) {}

AppRegistry.registerComponent(appName, () => App);
AppRegistry.runApplication(appName, {
  rootTag: document.getElementById("root"),
});
